import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Target, Plus, Trash2, CalendarClock, Trophy, Flag, Star } from "lucide-react";

const TYPES = [
  { value: "race", label: "Race", icon: Flag },
  { value: "event", label: "Event", icon: CalendarClock },
  { value: "milestone", label: "Milestone", icon: Trophy },
  { value: "personal", label: "Personal", icon: Star },
];

const typeIcon = (t) => (TYPES.find((x) => x.value === t)?.icon) || Target;

const countdownStyle = (days) => {
  if (days === null || days === undefined) return "bg-secondary text-muted-foreground border-border";
  if (days <= 7) return "bg-[#ccff0c] text-black border-[#ccff0c]";
  if (days <= 30) return "bg-[#ccff0c]/15 text-[#ccff0c] border-[#ccff0c]/30";
  return "bg-secondary text-muted-foreground border-border";
};

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", type: "race", date: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/goals").then(({ data }) => setGoals(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.date) { toast.error("Add a title and date"); return; }
    setSaving(true);
    try {
      await api.post("/goals", form);
      toast.success("Goal added");
      setForm({ title: "", type: "race", date: "", note: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not add goal");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/goals/${id}`); setGoals((g) => g.filter((x) => x.id !== id)); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <AppShell
      title="Goals"
      right={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-goal-button" size="sm" className="bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border max-w-[440px]">
            <DialogHeader><DialogTitle className="font-display">New goal</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Title</Label>
                <Input data-testid="goal-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. City Marathon" className="bg-card border-border h-11" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                    <SelectTrigger data-testid="goal-type" className="bg-card border-border h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border text-foreground">
                      {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Date</Label>
                  <Input data-testid="goal-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="bg-card border-border h-11" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Note (optional)</Label>
                <Textarea data-testid="goal-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Target time, why it matters…" className="bg-card border-border" />
              </div>
              <Button data-testid="save-goal" onClick={create} disabled={saving} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">Save goal</Button>
            </div>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="rd-fade-in pb-28">
        <p className="text-muted-foreground text-sm mb-4">Track your races, events, and milestones — with a live countdown to keep you locked in.</p>
        {goals.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-center" data-testid="goals-empty">
            <div className="h-14 w-14 rounded-2xl bg-[#ccff0c]/15 flex items-center justify-center mb-4">
              <Target className="h-7 w-7 text-[#ccff0c]" />
            </div>
            <p className="text-muted-foreground text-sm max-w-[260px]">No goals yet. Add your next race or milestone to start the countdown.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="goals-list">
            {goals.map((g) => {
              const Icon = typeIcon(g.type);
              return (
                <Card key={g.id} data-testid={`goal-${g.id}`} className="rounded-2xl bg-card border-border p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-xl bg-[#ccff0c]/15 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-[#ccff0c]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{g.title}</p>
                        <p className="text-xs text-muted-foreground">{new Date(g.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</p>
                        {g.note && <p className="text-xs text-muted-foreground mt-0.5 truncate">{g.note}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge className={`border font-mono ${countdownStyle(g.days_left)}`}>
                        {g.days_left === 0 ? "Today!" : `${g.days_left}d`}
                      </Badge>
                      <button data-testid={`goal-delete-${g.id}`} onClick={() => remove(g.id)} className="text-muted-foreground hover:text-red-400 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
